/* global $ */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import * as XLSX from "xlsx";
import axios from 'axios';
import Swal from 'sweetalert2';
import { format } from 'date-fns';
import axiosInstance from '../axiosInstance';

function Viewfinance() {
    const navigate = useNavigate();
    const location = useLocation();
    const existingData = location.state?.row;
    const [previousData, setpreviousData] = useState({});
    const [dueReceiptData, setDueReceiptData] = useState(null);
    const [loadingDueReceipt, setLoadingDueReceipt] = useState(false);
    const [dueReceiptError, setDueReceiptError] = useState(null);

    const [seizedHistory, setSeizedHistory] = useState([]);
    const [loadingSeized, setLoadingSeized] = useState(false);
    const [seizedError, setSeizedError] = useState(null);
    const isClosed = existingData?.isclosed === true || existingData?.isclosed === 1; // or however your closed status is stored

    useEffect(() => {
        if (existingData) {
            const financeId = existingData.id;

            const fetchSeizedHistory = async () => {
                try {
                    setLoadingSeized(true);
                    setSeizedError(null);
                    const res = await axiosInstance.get(`/seizedvehicle/history/${financeId}`);
                    setSeizedHistory(res.data.data || []);
                    console.log("Seizedvehicle: ", res.data.data)
                } catch (err) {
                    console.error("Error fetching seized history:", err);
                    setSeizedHistory([]);
                } finally {
                    setLoadingSeized(false);
                }
            };

            fetchSeizedHistory();
        }
    }, [existingData]);

    useEffect(() => {
        if (existingData) {
            const financeId = existingData.id;

            const fetchPreviousDetails = async () => {
                try {
                    const response = await axiosInstance.get(`/receipt/previousdetails/${financeId}`);
                    console.log("exisitingrecords : ", response.data)
                    setpreviousData(response.data);

                } catch (error) {
                    console.error('Error fetching previous data:', error);
                    setpreviousData([]);
                }
            };

            const fetchDueReceiptDetails = async () => {
                try {
                    setLoadingDueReceipt(true);
                    setDueReceiptError(null);

                    const response = await axiosInstance.get(`/duereceipt/${financeId}`);
                    console.log("DueRecipt: ", response)
                    setDueReceiptData(response.data);

                } catch (error) {
                    console.error('Error fetching due receipt data:', error);
                    setDueReceiptError('Failed to load due receipt details');
                    setDueReceiptData([]);
                } finally {
                    setLoadingDueReceipt(false);
                }
            };

            fetchPreviousDetails();
            fetchDueReceiptDetails();
        }
    }, [existingData]);

    const formatCurrency = (amount) => {
        if (!amount) return '₹0.00';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(amount);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    // Function to determine what amount to display in the Amount column     
    const getDisplayAmount = (receipt, index, allReceipts) => {
        // If no payment was made for this due
        if (!receipt.paid_amount || receipt.paid_amount <= 0) {
            return '-';
        }

        // If this is not a group payment (single due payment)
        if (!receipt.is_group_payment) {
            // Show the total payment amount for individual payments too
            return formatCurrency(receipt.total_payment_for_group || receipt.paid_amount);
        }

        // For group payments, show total only for the first occurrence of this receipt_id
        const firstOccurrenceIndex = allReceipts.findIndex(r =>
            r.receipt_id === receipt.receipt_id && r.receipt_id !== null
        );

        if (index === firstOccurrenceIndex) {
            // This is the first due in the group payment - show total payment amount
            return formatCurrency(receipt.total_payment_for_group);
        } else {
            // This is a subsequent due in the group payment - show empty or dash
            return '';
        }
    };

    // Function to determine what to display in the Previous Balance column
    const getPreviousBalanceDisplay = (receipt, index, allReceipts) => {
        // If no payment was made for this due
        if (!receipt.paid_amount || receipt.paid_amount <= 0) {
            // Get previous due's balance directly from the receipt data
            return receipt.previous_balance > 0 ? formatCurrency(receipt.previous_balance) : '-';
        }

        // If this is not a group payment (single due payment)
        if (!receipt.is_group_payment) {
            // Get previous due's balance directly from the receipt data
            return receipt.previous_balance > 0 ? formatCurrency(receipt.previous_balance) : '-';
        }

        // For group payments, show previous balance only for the first occurrence of this receipt_id
        const firstOccurrenceIndex = allReceipts.findIndex(r =>
            r.receipt_id === receipt.receipt_id && r.receipt_id !== null
        );

        if (index === firstOccurrenceIndex) {
            // This is the first due in the group payment - show previous balance
            return receipt.previous_balance > 0 ? formatCurrency(receipt.previous_balance) : '-';
        } else {
            // This is a subsequent due in the group payment - show dash
            return '-';
        }
    };

    // Function to determine if previous balance should be styled in green
    const shouldStylePreviousBalanceGreen = (receipt, index, allReceipts) => {
        // If no payment was made for this due
        if (!receipt.paid_amount || receipt.paid_amount <= 0) {
            return receipt.previous_balance > 0;
        }

        // If this is not a group payment (single due payment)
        if (!receipt.is_group_payment) {
            return receipt.previous_balance > 0;
        }

        // For group payments, style green only for the first occurrence with previous balance
        const firstOccurrenceIndex = allReceipts.findIndex(r =>
            r.receipt_id === receipt.receipt_id && r.receipt_id !== null
        );

        if (index === firstOccurrenceIndex) {
            return receipt.previous_balance > 0;
        }

        return false;
    };

    return (
        <div className="content-wrapper">
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col-sm-6">
                            <h1 className="m-0">View Finance</h1>
                        </div>
                        <div className="col-sm-6">
                            <ol className="breadcrumb float-sm-right">
                                <li className="breadcrumb-item"><a href="/">Home</a></li>
                                <li className="breadcrumb-item"><a href="/finance">Finance</a></li>
                                <li className="breadcrumb-item active">View</li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            {existingData?.hp_number && (
                <div className="row mb-2 mt-2">
                    <div className="col-12">
                        <div className="alert alert-info">
                            <strong><i className="fas fa-hashtag mr-1"></i>HP / Loan No:</strong> {existingData.hp_number}
                        </div>
                    </div>
                </div>
            )}

            <div className="row mb-2 mt-2">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="row">
                                <div className="col-6">
                                    <dl className="row">
                                        <dt className="col-sm-4 text-primary">Buyer Details</dt>
                                        <dd className="col-sm-8"></dd>
                                        <dt className="col-sm-4">Name:</dt>
                                        <dd className="col-sm-8">{existingData?.borrower_name}</dd>
                                        <dt className="col-sm-4">Address:</dt>
                                        <dd className="col-sm-8">{existingData?.borrower_address1}, {existingData?.borrower_address2}</dd>
                                        <dd className="col-sm-8 offset-sm-4">{existingData?.borrower_address3}, {existingData?.borrower_city}.</dd>
                                        <dt className="col-sm-4">Office Address:</dt>
                                        <dd className="col-sm-8">{existingData?.office_address1}, {existingData?.office_address2}</dd>
                                        <dd className="col-sm-8 offset-sm-4">{existingData?.office_address3}, {existingData?.office_city}.</dd>
                                        <dt className="col-sm-4">Phone Number:</dt>
                                        <dd className="col-sm-8">{existingData?.borrower_phone1}, {existingData?.borrower_phone2}</dd>
                                        <dt className="col-sm-4">Landmark:</dt>
                                        <dd className="col-sm-8">{existingData?.landmark}</dd>
                                        <dt className="col-sm-4">Father Name:</dt>
                                        <dd className="col-sm-8">{existingData?.father_name}</dd>
                                        <dt className="col-sm-4">Spouse Name:</dt>
                                        <dd className="col-sm-8">{existingData?.spouse_name}</dd>
                                        <dt className="col-sm-4">Profession:</dt>
                                        <dd className="col-sm-8">{existingData?.profession}</dd>
                                        <dt className="col-sm-4">House Type:</dt>
                                        <dd className="col-sm-8">{existingData?.house_type}</dd>
                                        <dt className="col-sm-4"></dt>
                                        <dd className="col-sm-8"></dd>
                                        <dt className="col-sm-4 text-primary">Guarantor Details</dt>
                                        <dd className="col-sm-8"></dd>
                                        <dt className="col-sm-4">Name:</dt>
                                        <dd className="col-sm-8">{existingData?.guarantor_name}</dd>
                                        <dt className="col-sm-4">Address:</dt>
                                        <dd className="col-sm-8">{existingData?.guarantor_address1}, {existingData?.guarantor_address2}</dd>
                                        <dd className="col-sm-8 offset-sm-4">{existingData?.guarantor_address3}, {existingData?.guarantor_city}.</dd>
                                        <dt className="col-sm-4">Phone Number:</dt>
                                        <dd className="col-sm-8">{existingData?.guarantor_phone1}, {existingData?.guarantor_phone2}</dd>
                                        <dt className="col-sm-4 text-primary">Due Details</dt>
                                        <dd className="col-sm-8"></dd>
                                        <dt className="col-sm-4">Loan Amount Paid:</dt>
                                        <dd className="col-sm-8">{previousData.totalpaiddue}</dd>
                                        <dt className="col-sm-4">Loan Amount Balance:</dt>
                                        <dd className="col-sm-8">{previousData.totalbalancedue}</dd>
                                        <dt className="col-sm-4">Total Due:</dt>
                                        <dd className="col-sm-8">{previousData.totalduecount}</dd>
                                        <dt className="col-sm-4">Paid Due:</dt>
                                        <dd className="col-sm-8">{previousData.due_receipt_count}</dd>
                                        <dt className="col-sm-4">UnPaid Due:</dt>
                                        <dd className="col-sm-8">{previousData.balanceduecount}</dd>
                                        <dt className="col-sm-4">Late Fine Amount:</dt>
                                        <dd className="col-sm-8">{previousData.calculated_fine}</dd>
                                    </dl>
                                </div>
                                <div className="col-6">
                                    <dl className="row">
                                        <dt className="col-sm-4 text-primary">Vehicle Details</dt>
                                        <dd className="col-sm-8"></dd>
                                        <dt className="col-sm-4">Registration Number:</dt>
                                        <dd className="col-sm-8">{existingData?.reg_no}</dd>
                                        <dt className="col-sm-4">Model & Color:</dt>
                                        <dd className="col-sm-8">{existingData?.model}, {existingData?.color}</dd>
                                        <dt className="col-sm-4">Make Year:</dt>
                                        <dd className="col-sm-8">{existingData?.make_year}</dd>
                                        <dt className="col-sm-4">Chasis Number:</dt>
                                        <dd className="col-sm-8">{existingData?.chase_no}</dd>
                                        <dt className="col-sm-4">Engine Number:</dt>
                                        <dd className="col-sm-8">{existingData?.engine_no}</dd>
                                        <dt className="col-sm-4">Description:</dt>
                                        <dd className="col-sm-8">{existingData?.description}</dd>
                                        <dt className="col-sm-4"></dt>
                                        <dd className="col-sm-8"></dd>
                                        <dt className="col-sm-4 text-primary">Finance Details </dt>
                                        <dd className="col-sm-8"></dd>
                                        <dt className="col-sm-4">Finance Date:</dt>
                                        <dd className="col-sm-8">{new Date(existingData?.date).toLocaleDateString("en-GB")}</dd>
                                        <dt className="col-sm-4">Loan Amount:</dt>
                                        <dd className="col-sm-8">{existingData?.loan_amount}</dd>
                                        <dt className="col-sm-4">Tenure Months:</dt>
                                        <dd className="col-sm-8">{existingData?.tenure_months}</dd>
                                        <dt className="col-sm-4">Monthly Due Date:</dt>
                                        <dd className="col-sm-8">{existingData?.due_date}</dd>
                                        <dt className="col-sm-4">Monthly Principal Amount:</dt>
                                        <dd className="col-sm-8">{existingData?.principal_amount}</dd>
                                        <dt className="col-sm-4">Monthly Interest Amount:</dt>
                                        <dd className="col-sm-8">{existingData?.interest_amount}</dd>
                                        <dt className="col-sm-4">Monthly EMI:</dt>
                                        <dd className="col-sm-8">{existingData?.emi_amount}</dd>
                                        <dt className="col-sm-4">Penalty Per Day:</dt>
                                        <dd className="col-sm-8">{existingData?.penalty_perday}</dd>
                                        <dt className="col-sm-4">Document Charges:</dt>
                                        <dd className="col-sm-8">{existingData?.document_charges}</dd>
                                        <dt className="col-sm-4">Loan Remark:</dt>
                                        <dd className="col-sm-8">{existingData?.loan_remark}</dd>
                                    </dl>
                                </div>
                            </div>

                            <hr className="divider"></hr>
                        </div>
                    </div>
                </div>
            </div>

            {/* Due Receipt Statement Section */}
            <div className="row mb-2 mt-2">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header">
                            <h3 className="card-title">
                                <i className="fas fa-file-invoice-dollar mr-2"></i>
                                Due Receipt Statement
                            </h3>
                        </div>
                        <div className="card-body">
                            {loadingDueReceipt ? (
                                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="sr-only">Loading...</span>
                                    </div>
                                </div>
                            ) : dueReceiptError ? (
                                <div className="alert alert-danger" role="alert">
                                    <i className="fas fa-exclamation-triangle mr-2"></i>
                                    {dueReceiptError}
                                </div>
                            ) : dueReceiptData ? (
                                <>
                                    {/* Summary Cards */}
                                    <div className="row mb-4">
                                        <div className="col-md-3">
                                            <div className="info-box bg-info">
                                                <span className="info-box-icon"><i className="fas fa-calendar-alt"></i></span>
                                                <div className="info-box-content">
                                                    <span className="info-box-text">Total Due Amount</span>
                                                    <span className="info-box-number">
                                                        {formatCurrency(dueReceiptData.summary?.total_due_amount)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="info-box bg-success">
                                                <span className="info-box-icon"><i className="fas fa-check-circle"></i></span>
                                                <div className="info-box-content">
                                                    <span className="info-box-text">Total Received</span>
                                                    <span className="info-box-number">
                                                        {formatCurrency(dueReceiptData.summary?.dash_total_received)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="info-box bg-warning">
                                                <span className="info-box-icon"><i className="fas fa-clock"></i></span>
                                                <div className="info-box-content">
                                                    <span className="info-box-text">Outstanding Balance</span>
                                                    <span className="info-box-number">
                                                        {formatCurrency(dueReceiptData.summary?.last_due_balance)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="info-box bg-danger">
                                                <span className="info-box-icon"><i className="fas fa-exclamation-triangle"></i></span>
                                                <div className="info-box-content">
                                                    <span className="info-box-text">Fine Balance</span>
                                                    <span className="info-box-number">
                                                        {formatCurrency(dueReceiptData.summary?.current_late_fine_balance)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Statement Table */}
                                    <div className="table-responsive">
                                        <table className="table table-striped table-bordered">
                                            <thead className="thead-dark">
                                                <tr>
                                                    <th>Due No.</th>
                                                    <th>Due Date</th>
                                                    <th>Description</th>
                                                    <th>Paid Date</th>
                                                    <th className="text-right">Paid Amount</th>
                                                    <th className="text-right">Due Amount</th>
                                                    <th className="text-right">Previous Balance</th>
                                                    <th className="text-right">Current Due</th>
                                                    <th className="text-right">Balance</th>
                                                    <th className="text-right">Fine</th>
                                                    <th className="text-right">Total Fine</th>
                                                    <th className="text-right">Paid Fine</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {/* Due Receipt Rows */}
                                                {dueReceiptData.due_receipts?.map((receipt, index) => {
                                                    const displayAmount = getDisplayAmount(receipt, index, dueReceiptData.due_receipts);

                                                    return (
                                                        <tr key={receipt.id}>
                                                            <td className="font-weight-bold">{receipt.due_no}</td>
                                                            <td>{formatDate(receipt.due_date)}</td>
                                                            <td>
                                                                Due {receipt.due_no}
                                                                {receipt.fine_days > 0 && (
                                                                    <div className="text-danger small mt-1">
                                                                        <i className="fas fa-exclamation-triangle mr-1"></i>
                                                                        Late by {receipt.fine_days} days
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td>
                                                                {receipt.paid_date ? formatDate(receipt.paid_date) : '-'}
                                                            </td>
                                                            <td className="text-right font-weight-bold text-info">
                                                                {displayAmount}
                                                            </td>
                                                            <td className="text-right font-weight-bold">
                                                                {formatCurrency(receipt.due_amt)}
                                                            </td>
                                                            <td className={shouldStylePreviousBalanceGreen(receipt, index, dueReceiptData.due_receipts) ? 'text-success font-weight-bold text-right' : 'text-right'}>
                                                                {getPreviousBalanceDisplay(receipt, index, dueReceiptData.due_receipts)}
                                                            </td>
                                                            <td className="text-right text-success font-weight-bold">
                                                                {formatCurrency(receipt.paid_amount)}
                                                            </td>
                                                            <td className={`text-right font-weight-bold ${receipt.due_bal > 0 ? 'text-danger' : 'text-success'}`}>
                                                                {formatCurrency(receipt.due_bal)}
                                                            </td>
                                                            <td className="text-right">
                                                                {receipt.fine > 0 ? (
                                                                    <span className="text-danger font-weight-bold">
                                                                        {formatCurrency(receipt.fine)}
                                                                    </span>
                                                                ) : '-'}
                                                            </td>
                                                            <td className="text-right">
                                                                {receipt.balance_fine > 0 ? (
                                                                    <span className="text-danger font-weight-bold">
                                                                        {formatCurrency(receipt.balance_fine)}
                                                                    </span>
                                                                ) : '-'}
                                                            </td>
                                                            <td className="text-right">
                                                                {receipt.payed_fine > 0 ? (
                                                                    <span className="text-success font-weight-bold">
                                                                        {formatCurrency(receipt.payed_fine)}
                                                                    </span>
                                                                ) : '-'}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}

                                                {/* Summary Row */}
                                                <tr className="table-secondary font-weight-bold">
                                                    <td colSpan="5" className="font-weight-bold">Total</td>
                                                    <td className="text-right">
                                                        {formatCurrency(dueReceiptData.summary?.total_due_amount)}
                                                    </td>
                                                    <td className="text-right text-success">
                                                        {formatCurrency(dueReceiptData.summary?.last_previous_balance)}
                                                    </td>
                                                    <td className="text-right text-success">
                                                        {formatCurrency(dueReceiptData.summary?.total_received)}
                                                    </td>
                                                    <td className="text-right"></td>
                                                    <td className="text-right"></td>
                                                    <td className="text-right text-danger">
                                                        {formatCurrency(dueReceiptData.summary?.current_late_fine_balance)}
                                                    </td>
                                                    <td className="text-right text-success">
                                                        {formatCurrency(dueReceiptData.summary?.total_paid_fine)}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Footer */}
                                    <div className="mt-3 d-flex justify-content-between align-items-center text-muted">
                                        <small>
                                            Generated on {formatDate(new Date())} at {new Date().toLocaleTimeString()}
                                        </small>
                                    </div>
                                </>
                            ) : (
                                <div className="alert alert-info" role="alert">
                                    <i className="fas fa-info-circle mr-2"></i>
                                    No due receipt data available for this finance record.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Seized Vehicle History Section */}
            <div className="row mb-5 mt-2">
                <div className="col-12">
                    <div className="card border-info">
                        <div className="card-header bg-info text-white">
                            <h3 className="card-title">
                                <i className="fas fa-motorcycle mr-2"></i>
                                Seized Vehicle History
                            </h3>
                        </div>
                        <div className="card-body">
                            {loadingSeized ? (
                                <div className="text-center my-4">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="sr-only">Loading...</span>
                                    </div>
                                </div>
                            ) : seizedError ? (
                                <div className="alert alert-danger">
                                    <i className="fas fa-exclamation-circle mr-2"></i>{seizedError}
                                </div>
                            ) : seizedHistory.length > 0 ? (
                                <div className="table-responsive">
                                    <table className="table table-striped table-bordered table-hover">
                                        <thead className="thead-light">
                                            <tr>
                                                <th>#</th>
                                                <th>Reg No</th>
                                                <th>Name</th>
                                                <th>Loan Amount</th>
                                                <th>Seizing Charges</th>
                                                <th>Seized Date</th>
                                                <th>Seized By</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {seizedHistory.map((item, index) => (
                                                <tr key={item.id}>
                                                    <td>{index + 1}</td>
                                                    <td>{item.reg_no}</td>
                                                    <td>{item.name}</td>
                                                    <td>{formatCurrency(item.loan_amount)}</td>
                                                    <td>{formatCurrency(item.seizing_charges)}</td>
                                                    <td>{formatDate(item.seized_date)}</td>
                                                    <td>{item.seized_by_vehicle.fullname || '-'}</td>
                                                    <td>
                                                        {item.isactive ? (
                                                            <span className="badge badge-success">Active</span>
                                                        ) : (
                                                            <span className="badge badge-secondary">Old</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="alert alert-info">
                                    <i className="fas fa-info-circle mr-2"></i>No seized history available for this finance.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {isClosed && (
                <div className="row mt-4">
                    <div className="col-12">
                        <div className="card border-success">
                            <div className="card-header bg-success text-white">
                                <h3 className="card-title mb-0">
                                    <i className="fas fa-lock mr-2"></i>
                                    Finance Closure Summary
                                </h3>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    {/* Left Column */}
                                    <div className="col-md-6">
                                        <dl className="row">
                                            <dt className="col-sm-6">Closure Date:</dt>
                                            <dd className="col-sm-6">{formatDate(existingData?.closure_date)}</dd>

                                            <dt className="col-sm-6">Closure Type:</dt>
                                            <dd className="col-sm-6">
                                                <span className={`badge badge-pill ${existingData?.closure_type === 'Completed' ? 'badge-success' :
                                                        existingData?.closure_type === 'FullSettlement' ? 'badge-warning' :
                                                            existingData?.closure_type === 'Seized' ? 'badge-danger' :
                                                                'badge-secondary'
                                                    }`}>
                                                    {existingData?.closure_type}
                                                </span>
                                            </dd>

                                            <dt className="col-sm-6">Closure Amount Paid:</dt>
                                            <dd className="col-sm-6 text-success">{formatCurrency(existingData?.closure_amount)}</dd>

                                            <dt className="col-sm-6">Late Fine Paid at Closure:</dt>
                                            <dd className="col-sm-6">{formatCurrency(existingData?.closure_fine_amount)}</dd>
                                        </dl>
                                    </div>

                                    {/* Right Column */}
                                    <div className="col-md-6">
                                        <dl className="row">
                                            <dt className="col-sm-6">Remaining Principal (Settled):</dt>
                                            <dd className="col-sm-6">{formatCurrency(existingData?.balance_principal)}</dd>

                                            <dt className="col-sm-6">Remaining Interest (Settled):</dt>
                                            <dd className="col-sm-6">{formatCurrency(existingData?.balance_interest)}</dd>

                                            <dt className="col-sm-6">Total Balance at Time of Closure:</dt>
                                            <dd className="col-sm-6 text-danger font-weight-bold">
                                                {formatCurrency(existingData?.balance_amount)}
                                            </dd>

                                            <dt className="col-sm-6">Total Loan Paid (Before Closure):</dt>
                                            <dd className="col-sm-6">{formatCurrency(previousData?.totalpaiddue)}</dd>
                                        </dl>
                                    </div>
                                </div>

                                <hr />
                                <p className="text-muted mb-0">
                                    This finance was <strong>closed</strong> on <strong>{formatDate(existingData?.closure_date)}</strong> via <strong>{existingData?.closure_type}</strong>.   All remaining balances shown above were settled as part of this closure.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}


            {isClosed && (
                <div className="closed-stamp">
                    CLOSED
                </div>
            )}


        </div>
    )
}

export default Viewfinance